/**
 * Карточка поездки в кабинете агентства.
 *
 * Что рисовать из действий, по-прежнему решает `agentStep` с бэка, а не догадки по статусу:
 * один и тот же шаг брони открывается и в первый раз, и после возврата кадровиком, поэтому
 * причина возврата приходит отдельным полем. Статус и этап считает `tripView` — ровно те же
 * правила, что в списке, иначе карточка и список рассказывали бы разное об одной поездке.
 *
 * Главное действие собрано в одну закреплённую панель внизу: раньше кнопки жили внутри
 * блоков, и на длинной карточке до них надо было доскроллить, угадав, какая из них главная.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocalSearchParams } from 'expo-router'
import { useState } from 'react'
import { Linking, Platform } from 'react-native'
import { Stack, Text, XStack, YStack } from 'tamagui'

import {
  api,
  type CabinetAttachment,
  type CabinetTrip,
  type CabinetTripVariant,
} from '../../../src/api/client'
import { CabinetBack, CabinetShell } from '../../../src/features/cabinet/CabinetShell'
import { TRIP_STAGES, tripStage, tripStatus } from '../../../src/features/cabinet/tripView'
import { errorText } from '../../../src/features/edo/errorText'
import { plural, useApp } from '../../../src/i18n'
import { fmtDateRange, fmtDmy, fmtKb, nightsBetween } from '../../../src/lib/format'
import { downloadWithAuth, pickFileWeb } from '../../../src/lib/files'
import { useResponsive } from '../../../src/lib/responsive'
import { tone } from '../../../src/theme/tones'
import {
  Avatar,
  Button,
  Card,
  FormInput,
  Icon,
  Link,
  OptionSelect,
  QueryState,
  SectionCard,
  SkeletonLines,
  Tag,
} from '../../../src/ui'
import { pressable } from '../../../src/ui/primitives'

const isWeb = Platform.OS === 'web'
const CURRENCIES = ['AZN', 'USD', 'EUR']

/** Время реплики треда — короткой строкой, без секунд и таймзонной арифметики. */
const fmtWhen = (iso: string) => iso.replace('T', ' ').slice(0, 16)

interface OptionRow {
  flight: string
  cabin: string
  hotel: string
  price: string
}
const EMPTY_ROW: OptionRow = { flight: '', cabin: '', hotel: '', price: '' }

const rowsValid = (rows: OptionRow[]) =>
  rows.length > 0 && rows.every((r) => r.flight.trim() && r.hotel.trim() && Number(r.price) > 0)

function Row({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <XStack gap={10} alignItems="flex-start">
      <Text fontSize={13} color="$muted" width={120}>
        {label}
      </Text>
      <Text fontSize={13} color="$color" flex={1}>
        {value}
      </Text>
    </XStack>
  )
}

/** Полоса этапов. Точного этапа бэк не отдаёт — он выводится из `state` и `agentStep`. */
function StageBar({ stage }: { stage: number }) {
  const { l } = useApp()
  return (
    <Card padding={16}>
      <XStack aria-label={l('cab.trip.stages')} gap={8} flexWrap="wrap">
        {TRIP_STAGES.map((key, i) => {
          const done = i < stage
          const cur = i === stage
          return (
            <XStack key={key} flex={1} minWidth={140} alignItems="center" gap={10}>
              <Stack
                width={26}
                height={26}
                borderRadius={999}
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                backgroundColor={done ? tone('green').solid : cur ? '$primary' : '$borderSecondary'}
              >
                {done ? (
                  <Icon name="check" size={14} color="#fff" />
                ) : (
                  <Text fontSize={12} fontWeight="700" color={cur ? '$onPrimary' : '$muted'}>
                    {i + 1}
                  </Text>
                )}
              </Stack>
              <Text
                fontSize={13}
                fontWeight={cur ? '700' : '400'}
                color={cur ? '$color' : done ? '$color' : '$muted'}
              >
                {l(`cab.trip.stage.${key}`)}
              </Text>
            </XStack>
          )
        })}
      </XStack>
    </Card>
  )
}

/**
 * «Что от вас нужно» — первый блок после этапов и единственное место, где сказано прямым
 * текстом, чего ждут. Цвет тот же, что у пилюли статуса: жёлтый — действие, красный — возврат,
 * синий — ход у компании.
 */
function NeedBox({ trip }: { trip: CabinetTrip }) {
  const { l } = useApp()
  const st = tripStatus(trip)
  const key =
    trip.agentStep === 'options'
      ? 'options'
      : trip.agentStep === 'booking'
        ? trip.returnedReason
          ? 'returned'
          : 'booking'
        : st.kind === 'done'
          ? 'done'
          : 'wait'
  return (
    <YStack
      aria-label={l('cab.trip.need')}
      gap={6}
      padding={16}
      borderRadius={12}
      backgroundColor={tone(st.tone).soft}
    >
      <XStack gap={10} alignItems="center">
        <Icon name="info" size={18} color={tone(st.tone).text} />
        <Text fontSize={16} fontWeight="700" color="$color" flex={1}>
          {l(`cab.trip.need.${key}.title`)}
        </Text>
      </XStack>
      {/* Причина возврата — словами кадровика, в кавычках: это не наш текст. */}
      {key === 'returned' && !!trip.returnedReason && (
        <Text fontSize={14} color="$color" fontStyle="italic">
          {`«${trip.returnedReason}»`}
        </Text>
      )}
      <Text fontSize={14} color="$color" lineHeight={20}>
        {l(`cab.trip.need.${key}.text`)}
      </Text>
    </YStack>
  )
}

/** Варианты, уже отправленные сотруднику: у выбранного рамка и бейдж, остальные приглушены. */
function Variants({ trip }: { trip: CabinetTrip }) {
  const { l } = useApp()
  return (
    <SectionCard
      title={l('cab.trips.variants')}
      extra={
        trip.choice ? (
          <Text fontSize={12} color="$muted">
            {l(trip.choice.by === 'auto' ? 'cab.trips.chosen.auto' : 'cab.trips.chosen.employee')}
          </Text>
        ) : undefined
      }
    >
      <YStack gap={10}>
        {trip.variants.map((v, i) => {
          const chosen = trip.choice?.index === i
          return (
            <XStack
              key={i}
              gap={12}
              alignItems="center"
              padding={14}
              borderRadius={10}
              borderWidth={chosen ? 2 : 1}
              borderColor={chosen ? '$primary' : '$borderColor'}
              opacity={trip.choice && !chosen ? 0.65 : 1}
            >
              <YStack flex={1} minWidth={0} gap={3}>
                <Text fontSize={14} fontWeight="700" color="$color">
                  {v.flight}
                </Text>
                <Text fontSize={13} color="$muted">
                  {[v.cabin, v.hotel].filter(Boolean).join(' · ')}
                </Text>
              </YStack>
              <YStack alignItems="flex-end" gap={6}>
                <Text fontSize={15} fontWeight="700" color="$color">
                  {`${v.price} ${v.currency}`}
                </Text>
                {chosen && <Tag tone="blue">{l('cab.trip.chosenBadge')}</Tag>}
              </YStack>
            </XStack>
          )
        })}
      </YStack>
    </SectionCard>
  )
}

/** Форма вариантов: подписи у полей, валюта одна на все строки — её выбирают один раз. */
function OptionsForm({
  rows,
  setRows,
  currency,
  setCurrency,
}: {
  rows: OptionRow[]
  setRows: (next: OptionRow[]) => void
  currency: string
  setCurrency: (c: string) => void
}) {
  const { l } = useApp()
  const field = (i: number, key: keyof OptionRow) => (v: string) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, [key]: v } : r)))

  return (
    <SectionCard
      title={l('cab.trips.options.title')}
      extra={
        <Text fontSize={12} color="$muted">
          {l('cab.trips.options.hint')}
        </Text>
      }
    >
      <YStack gap={12}>
        <YStack gap={4}>
          <Text fontSize={12} color="$muted">
            {l('cab.trips.options.money')}
          </Text>
          <OptionSelect
            options={CURRENCIES}
            value={[currency]}
            onChange={(v) => setCurrency(v[0] ?? currency)}
          />
        </YStack>
        {rows.map((r, i) => (
          <YStack key={i} gap={8} padding={12} borderWidth={1} borderColor="$borderColor" borderRadius={10}>
            <XStack alignItems="center" justifyContent="space-between">
              <Text fontSize={13} fontWeight="600" color="$muted">
                {`${l('cab.trip.stage.options')} ${i + 1}`}
              </Text>
              {rows.length > 1 && (
                <Stack
                  {...pressable}
                  aria-label={l('cab.trips.options.remove')}
                  onPress={() => setRows(rows.filter((_, j) => j !== i))}
                  width={44}
                  height={44}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon name="x" size={16} color="$muted" />
                </Stack>
              )}
            </XStack>
            <Field
              label={l('cab.trips.options.flight')}
              example={l('cab.trips.options.flightEx')}
              value={r.flight}
              onChange={field(i, 'flight')}
            />
            <Field
              label={l('cab.trips.options.cabin')}
              example={l('cab.trips.options.cabinEx')}
              value={r.cabin}
              onChange={field(i, 'cabin')}
            />
            <Field
              label={l('cab.trips.options.hotel')}
              example={l('cab.trips.options.hotelEx')}
              value={r.hotel}
              onChange={field(i, 'hotel')}
            />
            <Field
              label={`${l('cab.trips.options.price')}, ${currency}`}
              example={l('cab.trips.options.priceEx')}
              value={r.price}
              onChange={field(i, 'price')}
              numeric
            />
          </YStack>
        ))}
        <Button height={44} variant="default" onPress={() => setRows([...rows, EMPTY_ROW])}>
          {l('cab.trips.options.add')}
        </Button>
      </YStack>
    </SectionCard>
  )
}

/**
 * Поле с подписью. Раньше у полей кабинета был только placeholder: он исчезает при вводе, а
 * на экране ридера не читается вовсе — отсюда и `aria-label`. Высота 44 — минимальный
 * тач-таргет; базовый `FormInput` ниже, он рассчитан на плотные формы портала.
 */
function Field({
  label,
  example,
  value,
  onChange,
  numeric,
}: {
  label: string
  /** Пример формата: `flight` и `hotel` на бэке — свободный текст, вид им задаёт только он. */
  example?: string
  value: string
  onChange: (v: string) => void
  numeric?: boolean
}) {
  return (
    <YStack gap={4}>
      <Text fontSize={12} color="$muted">
        {label}
      </Text>
      <FormInput
        value={value}
        onChangeText={(v: string) => onChange(numeric ? v.replace(/[^0-9.]/g, '') : v)}
        placeholder={example}
        aria-label={label}
        height={44}
        fontSize={14}
        maxLength={numeric ? 12 : 200}
        {...(numeric ? { keyboardType: 'numeric' as const } : null)}
      />
    </YStack>
  )
}

/**
 * Документы брони: что уже приложено (с сервера — список переживает перезагрузку и возврат) и
 * зона загрузки, пока шаг за агентством. Удаления нет — у бэка нет ручки.
 */
function BookingDocs({
  files,
  onAttach,
  busy,
}: {
  files: CabinetAttachment[]
  /** Нет — шаг брони не за агентством: список только для чтения. */
  onAttach?: () => void
  busy: boolean
}) {
  const { l, t } = useApp()
  const [dlErr, setDlErr] = useState('')
  const download = (a: CabinetAttachment) => {
    setDlErr('')
    void downloadWithAuth(() => api.cabinetAttachment(a.uploadId), a.name).catch((e) =>
      setDlErr(t(errorText(e))),
    )
  }
  return (
    <SectionCard title={l('cab.trips.booking.title')}>
      <YStack gap={10}>
        {files.map((a) => (
          <XStack key={a.uploadId} gap={10} alignItems="center" padding={10} borderRadius={8} backgroundColor="$bgLayout">
            <Icon name="paperclip" size={16} color="$muted" />
            <YStack flex={1} minWidth={0} gap={2}>
              <Text fontSize={14} color="$color">
                {a.name}
              </Text>
              <Text fontSize={12} color="$muted">
                {`${fmtKb(a.size)} · ${fmtDmy(a.createdAt)}`}
              </Text>
            </YStack>
            {/* Скачивание — web-only: нативного файлового слоя в проекте нет. */}
            {isWeb && (
              <Button size="sm" variant="default" onPress={() => download(a)}>
                {l('cab.files.download')}
              </Button>
            )}
          </XStack>
        ))}
        {!!dlErr && (
          <Text fontSize={12} color={tone('red').text}>
            {dlErr}
          </Text>
        )}
        {onAttach &&
          (isWeb ? (
            <>
              <YStack
                {...pressable}
                onPress={busy ? undefined : onAttach}
                alignItems="center"
                gap={6}
                padding={22}
                borderWidth={1}
                borderColor="$borderColor"
                borderStyle="dashed"
                borderRadius={10}
                opacity={busy ? 0.6 : 1}
              >
                <Text fontSize={14} fontWeight="600" color="$primary">
                  {l(busy ? 'cab.files.attaching' : 'cab.trips.booking.attach')}
                </Text>
                <Text fontSize={12} color="$muted">
                  {l('cab.trips.booking.hint')}
                </Text>
              </YStack>
              {/* Файл уходит в заявку сразу при выборе, а шаг закрывает кнопка в панели внизу —
                  без этой строки агент искал кнопку «отправить файл». */}
              <Text fontSize={12} color="$muted">
                {l('cab.trips.booking.saved')}
              </Text>
            </>
          ) : (
            <Text fontSize={12} color="$muted">
              {l('cab.trips.booking.webonly')}
            </Text>
          ))}
      </YStack>
    </SectionCard>
  )
}

/** Тред с куратором HR — единственный выход агента: вернуть заявку или отказаться он не может. */
function CuratorThread({
  tripId,
  thread,
  awaiting,
  onDone,
}: {
  tripId: string
  thread: CabinetTrip['thread']
  awaiting: boolean
  onDone: () => void
}) {
  const { l, t } = useApp()
  const [text, setText] = useState('')
  const ask = useMutation({
    mutationFn: () => api.cabinetTripQuestion(tripId, text.trim()),
    onSuccess: () => {
      setText('')
      onDone()
    },
  })
  return (
    <SectionCard title={l('cab.trips.thread.title')}>
      <YStack gap={8}>
        {(thread ?? []).map((m, i) => (
          <YStack
            key={i}
            gap={2}
            backgroundColor={m.fromAgency ? '$bgLayout' : tone('blue').soft}
            borderRadius={8}
            padding={10}
          >
            <Text fontSize={12} color="$muted">
              {[m.author, fmtWhen(m.at)].filter(Boolean).join(' · ')}
            </Text>
            <Text fontSize={13} color="$color">
              {m.text}
            </Text>
          </YStack>
        ))}
        <Text fontSize={12} color={awaiting ? tone('gold').text : '$muted'}>
          {l(awaiting ? 'cab.trips.thread.pending' : 'cab.trips.thread.hint')}
        </Text>
        <FormInput
          value={text}
          onChangeText={setText}
          placeholder={l('cab.trips.thread.placeholder')}
          aria-label={l('cab.trips.thread.placeholder')}
          minHeight={64}
          height={undefined}
          fontSize={14}
          multiline
        />
        {ask.isError && (
          <Text fontSize={12} color={tone('red').text}>
            {t(errorText(ask.error))}
          </Text>
        )}
        <Button height={44} disabled={!text.trim() || ask.isPending} onPress={() => ask.mutate()}>
          {l('cab.trips.thread.send')}
        </Button>
      </YStack>
    </SectionCard>
  )
}

export default function CabinetTripScreen() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const { l, t, lang } = useApp()
  const { isPhone } = useResponsive()
  const qc = useQueryClient()

  const q = useQuery({
    queryKey: ['cabinet', 'trip', id],
    queryFn: () => api.cabinetTrip(String(id)),
    enabled: !!id,
  })
  const trip = q.data

  // Одна строка на старте: подсказка просит 2–3 варианта, но пустая вторая строка держала бы
  // кнопку выключенной, пока её не заполнят или не удалят.
  const [rows, setRows] = useState<OptionRow[]>([EMPTY_ROW])
  const [currency, setCurrency] = useState('AZN')
  const docs = trip?.attachments ?? []
  const [confirm, setConfirm] = useState(false)
  const [sent, setSent] = useState(false)
  const [err, setErr] = useState('')

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['cabinet', 'trip', id] })
    void qc.invalidateQueries({ queryKey: ['cabinet', 'trips'] })
  }
  const after = () => {
    setConfirm(false)
    setSent(true)
    refresh()
  }
  const fail = (e: unknown) => {
    setConfirm(false)
    setErr(t(errorText(e)))
  }

  const attach = useMutation({
    mutationFn: async () => {
      const file = await pickFileWeb('.pdf,.jpg,.jpeg,.png')
      if (!file) return null
      const form = new FormData()
      form.append('file', file as unknown as Blob)
      return api.cabinetTripAttach(String(id), form)
    },
    onSuccess: (r) => r && refresh(),
    onError: fail,
  })
  const sendOptions = useMutation({
    mutationFn: () =>
      api.cabinetTripOptions(
        String(id),
        rows.map(
          (r): CabinetTripVariant => ({
            flight: r.flight.trim(),
            cabin: r.cabin.trim() || undefined,
            hotel: r.hotel.trim(),
            price: Number(r.price),
            currency,
          }),
        ),
      ),
    onSuccess: after,
    onError: fail,
  })
  const sendBooking = useMutation({
    mutationFn: () => api.cabinetTripBooking(String(id)),
    onSuccess: after,
    onError: fail,
  })

  /** Главное действие шага: что за кнопка, активна ли она и чем подписана. */
  const action = !trip
    ? null
    : trip.agentStep === 'options'
      ? {
          label: l('cab.trips.options.send'),
          hint: l('cab.trips.options.locked'),
          ready: rowsValid(rows),
          run: () => sendOptions.mutate(),
          busy: sendOptions.isPending,
        }
      : trip.agentStep === 'booking'
        ? {
            label: l(trip.returnedReason ? 'cab.trips.booking.again' : 'cab.trips.booking.done'),
            hint: docs.length
              ? l('cab.trips.booking.files').replace('{n}', String(docs.length))
              : l('cab.trips.booking.need'),
            ready: docs.length > 0,
            run: () => sendBooking.mutate(),
            busy: sendBooking.isPending,
          }
        : null

  const st = trip ? tripStatus(trip) : null
  const nights = trip ? nightsBetween(trip.dateFrom, trip.dateTo) : null
  const subtitle = trip
    ? [
        fmtDateRange(trip.dateFrom, trip.dateTo, lang),
        nights != null ? plural(l('cab.trips.nights'), nights, lang) : '',
        trip.travellers.length ? plural(l('cab.trips.people'), trip.travellers.length, lang) : '',
        `№ ${trip.number}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : ''

  return (
    <CabinetShell section="trips">
      <CabinetBack to="trips" label={l('cab.trip.back')} />

      <QueryState q={q}>
        {q.isLoading || !trip || !st ? (
          <SkeletonLines lines={4} />
        ) : (
          <YStack gap={16}>
            <XStack gap={12} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap">
              <YStack gap={6} flexShrink={1}>
                <Text fontSize={26} fontWeight="800" color="$color" letterSpacing={-0.4}>
                  {[trip.originCityName, trip.destinationCityName].filter(Boolean).join(' → ') ||
                    `№ ${trip.number}`}
                </Text>
                <Text fontSize={14} color="$muted">
                  {subtitle}
                </Text>
              </YStack>
              <Tag tone={st.tone}>{l(st.pill)}</Tag>
            </XStack>

            <StageBar stage={tripStage(trip)} />
            <NeedBox trip={trip} />

            {sent && (
              <XStack gap={8} alignItems="center" padding={12} borderRadius={10} backgroundColor={tone('green').soft}>
                <Icon name="check-circle" size={16} color={tone('green').text} />
                <Text fontSize={14} fontWeight="600" color={tone('green').text}>
                  {l('cab.trip.sent')}
                </Text>
              </XStack>
            )}

            <XStack gap={16} flexWrap="wrap" alignItems="flex-start" flexDirection={isPhone ? 'column' : 'row'}>
              <YStack gap={16} flex={1} minWidth={260} width={isPhone ? '100%' : undefined}>
                <SectionCard title={l('cab.trip.about')}>
                  <YStack gap={10}>
                    <Row label={l('cab.trip.from')} value={trip.originCityName} />
                    <Row label={l('cab.trip.to')} value={trip.destinationCityName} />
                    <Row
                      label={l('cab.trip.dates')}
                      value={
                        trip.dateFrom && trip.dateTo
                          ? `${fmtDmy(trip.dateFrom)} — ${fmtDmy(trip.dateTo)}`
                          : fmtDmy(trip.dateFrom ?? trip.dateTo)
                      }
                    />
                    <Row label={l('cab.trip.address')} value={trip.destinationAddress} />
                  </YStack>
                </SectionCard>

                <SectionCard title={`${l('cab.trips.travellers')} · ${trip.travellers.length}`}>
                  <YStack gap={12}>
                    {trip.travellers.map((p, i) => (
                      <XStack key={i} gap={12} alignItems="center">
                        <Avatar name={p.name} size="md" />
                        <YStack gap={2} flex={1} minWidth={0}>
                          <Text fontSize={14} fontWeight="600" color="$color">
                            {p.name}
                          </Text>
                          {/* Телефон звонком: на вебе это <a href="tel:">, на телефоне —
                              Linking, иначе номер остался бы просто текстом. */}
                          {!!p.phone && (
                            <Link
                              fontSize={13}
                              {...(isWeb ? { tag: 'a' as const, href: `tel:${p.phone}` } : null)}
                              onPress={() => void Linking.openURL(`tel:${p.phone}`)}
                            >
                              {p.phone}
                            </Link>
                          )}
                          {!!p.passportValidUntil && (
                            <Text fontSize={12} color="$muted">
                              {`${l('cab.trips.passport')} ${fmtDmy(p.passportValidUntil)}`}
                            </Text>
                          )}
                        </YStack>
                      </XStack>
                    ))}
                  </YStack>
                </SectionCard>
              </YStack>

              <YStack gap={16} flex={2} minWidth={280} width={isPhone ? '100%' : undefined}>
                {trip.agentStep === 'options' ? (
                  <OptionsForm
                    rows={rows}
                    setRows={setRows}
                    currency={currency}
                    setCurrency={setCurrency}
                  />
                ) : (
                  trip.variants.length > 0 && <Variants trip={trip} />
                )}
                {/* После закрытия шага список остаётся: агенту нужна история своих броней. */}
                {(trip.agentStep === 'booking' || docs.length > 0) && (
                  <BookingDocs
                    files={docs}
                    onAttach={trip.agentStep === 'booking' ? () => attach.mutate() : undefined}
                    busy={attach.isPending}
                  />
                )}
                <CuratorThread
                  tripId={String(id)}
                  thread={trip.thread ?? []}
                  awaiting={!!trip.awaitingAnswer}
                  onDone={refresh}
                />
              </YStack>
            </XStack>

            {/* Закреплённая панель: на вебе прилипает к низу прокрутки, на нативе просто
                стоит последней — `sticky` там нет, а дублировать кнопку незачем. */}
            {action && (
              <YStack
                position={isWeb ? ('sticky' as 'relative') : 'relative'}
                bottom={0}
                gap={8}
                padding={14}
                borderRadius={12}
                borderWidth={1}
                borderColor="$borderColor"
                backgroundColor="$background"
              >
                {!!err && (
                  <Text fontSize={12} color={tone('red').text}>
                    {err}
                  </Text>
                )}
                {confirm ? (
                  <XStack gap={10} alignItems="center" flexWrap="wrap">
                    <Text fontSize={13} color="$color" flex={1} minWidth={180}>
                      {l('cab.trip.confirm')}
                    </Text>
                    <Button height={44} variant="default" onPress={() => setConfirm(false)}>
                      {l('cab.trip.cancel')}
                    </Button>
                    <Button
                      height={44}
                      disabled={action.busy}
                      onPress={() => {
                        if (!action.busy) action.run()
                      }}
                    >
                      {l('cab.trip.confirmYes')}
                    </Button>
                  </XStack>
                ) : (
                  <XStack gap={10} alignItems="center" flexWrap="wrap">
                    <Text fontSize={13} color="$muted" flex={1} minWidth={180}>
                      {action.hint}
                    </Text>
                    <Button
                      height={44}
                      disabled={!action.ready || action.busy}
                      onPress={() => {
                        // Своя проверка, а не только `disabled`: Tamagui снимает обработчик на
                        // вебе (нативный `disabled` у <button>), но на нативе нажатие доходит.
                        if (!action.ready || action.busy) return
                        setErr('')
                        setConfirm(true)
                      }}
                    >
                      {action.label}
                    </Button>
                  </XStack>
                )}
              </YStack>
            )}
          </YStack>
        )}
      </QueryState>
    </CabinetShell>
  )
}
